/**
 * ConfigurationForm Component
 *
 * Task 171: Configuration Form - Core Settings Section
 * Task 172: Configuration Form - Contract Structure Section
 * Task 173: Configuration Form - Basic Details Section
 * Task 174: Configuration Form - Key Issues Accordion
 * Task 175: Configuration Form - Base Document Upload
 *
 * Section A of the Drafting configuration form containing:
 * - Stance: 3 radio buttons (Customer-favoured / Balanced / Vendor-favoured)
 * - Complexity: 3 radio buttons (Simple / Moderate / Complex)
 * - Role & Context: textarea for purpose of contract
 * - Industry: dropdown with 7 options
 * - Product/Service Type: text input
 *
 * Section B of the Drafting configuration form containing:
 * - Structure Type: 3 radio buttons (Main Body only / + Commercial Terms / + Schedules)
 * - Commercial Position: Front/Back toggle (visible when Commercial Terms selected)
 * - Schedule Picker: 11 checkboxes (visible when Schedules selected)
 *
 * Section C of the Drafting configuration form containing:
 * - Title of Agreement: text input
 * - Commencement Date: date picker
 * - Jurisdiction: dropdown (Australian states + Other)
 * - Party 1: collapsible section with Name, Short name, Address, Company number
 * - Party 2: same structure as Party 1
 * - Third Parties: optional textarea
 * - Term: Start date, End date
 * - Auto-renewal: Yes/No toggle with renewal period and max renewals
 * - Dispute Resolution: dropdown
 *
 * Section D of the Drafting configuration form containing:
 * - Key Issues: 8 checkboxes with expandable follow-up questions
 *   (Payment, Termination, Liability, IP, Confidentiality, Data Protection, Warranties, Indemnification)
 *
 * Section E of the Drafting configuration form containing:
 * - Base Document Upload: drag-and-drop upload for precedent/playbook documents
 *   (PDF/DOCX, 100MB limit, processing status, chunk count display)
 *
 * Features:
 * - Form state management with useState
 * - All fields optional with sensible defaults
 * - onChange callback for parent integration
 * - initialValues prop for pre-populating form
 * - Conditional UI with smooth animations
 */

import React, { useState, useEffect, useCallback } from 'react';
import { DraftingStance, DraftingComplexity, StructureType, Schedule, PartyInfo, BasicIssues, KeyIssues } from '../../types/drafting';
import KeyIssuesAccordion from './KeyIssuesAccordion';
import BaseDocumentUpload, { UploadedDocument } from './BaseDocumentUpload';
import './ConfigurationForm.css';

// ==============================================================================
// Type Definitions
// ==============================================================================

/**
 * Form state for configuration form (Sections A + B + C + D + E)
 */
export interface CoreSettingsFormState {
  // Section A: Core Settings
  stance: DraftingStance;
  complexity: DraftingComplexity;
  context: string;
  industry: string;
  product_service: string;
  // Section B: Contract Structure
  structure_type: StructureType;
  commercial_position: 'front' | 'back';
  schedules: Schedule[];
  // Section C: Basic Details
  basic_issues: BasicIssues;
  // Section D: Key Issues
  key_issues: KeyIssues;
  // Section E: Base Document (optional)
  base_document?: UploadedDocument;
}

/**
 * Props for ConfigurationForm component
 */
export interface ConfigurationFormProps {
  /** Callback when any form field changes */
  onChange?: (formState: CoreSettingsFormState) => void;
  /** Initial values to pre-populate the form */
  initialValues?: Partial<CoreSettingsFormState>;
  /** Project ID for base document upload (optional - if not provided, operates in deferred mode) */
  projectId?: string;
  /**
   * Callback when a base document file is selected in deferred mode (no projectId).
   * The parent should store this file and upload it after creating the project.
   */
  onBaseDocumentSelected?: (file: File | null) => void;
  /**
   * When in deferred mode, the staged base document file to display.
   */
  stagedBaseDocument?: File | null;
  /**
   * Plan 185: suppress the built-in BaseDocumentUpload section so the parent
   * can render the multi-precedent PrecedentPicker in its place. When true,
   * onBaseDocumentSelected / stagedBaseDocument are ignored.
   */
  hideBaseDocument?: boolean;
  /**
   * Plan 253: the selected template. `sow` adds role headings, the master
   * agreement fields and a reduced key-issue set; other templates are
   * unchanged.
   */
  templateType?: string;
}

// ==============================================================================
// Constants
// ==============================================================================

const INDUSTRY_OPTIONS = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'other', label: 'Other' },
];

/** Plan 253: the key issues a Statement of Work's clauses use. */
const SOW_KEY_ISSUES = ['payment', 'ip'] as const;

/**
 * Jurisdiction options for Australian states/territories
 */
const JURISDICTION_OPTIONS = [
  { value: 'nsw', label: 'New South Wales (NSW)' },
  { value: 'vic', label: 'Victoria (VIC)' },
  { value: 'qld', label: 'Queensland (QLD)' },
  { value: 'wa', label: 'Western Australia (WA)' },
  { value: 'sa', label: 'South Australia (SA)' },
  { value: 'tas', label: 'Tasmania (TAS)' },
  { value: 'act', label: 'Australian Capital Territory (ACT)' },
  { value: 'nt', label: 'Northern Territory (NT)' },
  { value: 'other', label: 'Other' },
];

/**
 * Dispute resolution options
 */
const DISPUTE_RESOLUTION_OPTIONS = [
  { value: 'litigation', label: 'Litigation' },
  { value: 'mediation_then_litigation', label: 'Mediation then Litigation' },
  { value: 'arbitration', label: 'Arbitration' },
];

/**
 * Renewal period options for auto-renewal
 */
const RENEWAL_PERIOD_OPTIONS = [
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '12_months', label: '12 Months' },
  { value: '24_months', label: '24 Months' },
];

/**
 * Schedule options for schedule picker (Section B)
 */
const SCHEDULE_OPTIONS: { value: Schedule; label: string }[] = [
  { value: Schedule.SECURITY, label: 'Security' },
  { value: Schedule.GOVERNANCE, label: 'Governance' },
  { value: Schedule.SERVICE_MANAGEMENT, label: 'Service Management' },
  { value: Schedule.CONTRACT_CHANGES, label: 'Contract Changes' },
  { value: Schedule.EXIT_TRANSITION, label: 'Exit & Transition' },
  { value: Schedule.SLA, label: 'Support & Maintenance (SLA)' },
  { value: Schedule.POLICIES, label: 'Policies' },
  { value: Schedule.DISASTER_RECOVERY, label: 'Disaster Recovery / BCP' },
  { value: Schedule.FINANCIAL_OBLIGATIONS, label: 'Financial Obligations' },
  { value: Schedule.VALUE_ASSURANCE, label: 'Value Assurance' },
  { value: Schedule.RATE_CARDS, label: 'Professional Services Rate Cards' },
];

/**
 * Default party info structure
 */
const DEFAULT_PARTY: PartyInfo = {
  name: '',
  short_name: '',
  address: '',
  company_number: '',
};

/**
 * Default basic issues structure
 */
const DEFAULT_BASIC_ISSUES: BasicIssues = {
  title: '',
  commencement_date: '',
  jurisdiction: '',
  party1: { ...DEFAULT_PARTY },
  party2: { ...DEFAULT_PARTY },
  third_parties: '',
  term_start: '',
  term_end: '',
  auto_renewal: false,
  renewal_period: '',
  max_renewals: undefined,
  dispute_resolution: undefined,
};

const DEFAULT_STATE: CoreSettingsFormState = {
  // Section A: Core Settings
  stance: DraftingStance.BALANCED,
  complexity: DraftingComplexity.MODERATE,
  context: '',
  industry: '',
  product_service: '',
  // Section B: Contract Structure
  structure_type: StructureType.MAIN_ONLY,
  commercial_position: 'front',
  schedules: [],
  // Section C: Basic Details
  basic_issues: { ...DEFAULT_BASIC_ISSUES },
  // Section D: Key Issues
  key_issues: {},
  // Section E: Base Document
  base_document: undefined,
};

// ==============================================================================
// Component
// ==============================================================================

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({
  onChange,
  initialValues,
  projectId,
  onBaseDocumentSelected,
  stagedBaseDocument,
  hideBaseDocument,
  templateType,
}) => {
  const isSow = templateType === 'sow';
  // Merge initial values with defaults
  const [formState, setFormState] = useState<CoreSettingsFormState>(() => ({
    ...DEFAULT_STATE,
    ...initialValues,
  }));

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      onChange(formState);
    }
  }, [formState, onChange]);

  // Update form state and trigger onChange
  const updateField = <K extends keyof CoreSettingsFormState>(
    field: K,
    value: CoreSettingsFormState[K]
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Toggle schedule selection
  const toggleSchedule = (schedule: Schedule) => {
    setFormState((prev) => {
      const isSelected = prev.schedules.includes(schedule);
      const newSchedules = isSelected
        ? prev.schedules.filter((s) => s !== schedule)
        : [...prev.schedules, schedule];
      return {
        ...prev,
        schedules: newSchedules,
      };
    });
  };

  // Update basic_issues field
  const updateBasicIssue = <K extends keyof BasicIssues>(
    field: K,
    value: BasicIssues[K]
  ) => {
    setFormState((prev) => ({
      ...prev,
      basic_issues: {
        ...prev.basic_issues,
        [field]: value,
      },
    }));
  };

  // Update party info (party1 or party2)
  const updatePartyInfo = (
    party: 'party1' | 'party2',
    field: keyof PartyInfo,
    value: string
  ) => {
    setFormState((prev) => ({
      ...prev,
      basic_issues: {
        ...prev.basic_issues,
        [party]: {
          ...(prev.basic_issues[party] || DEFAULT_PARTY),
          [field]: value,
        },
      },
    }));
  };

  // Computed visibility flags
  const showCommercialPosition =
    formState.structure_type === StructureType.WITH_COMMERCIAL ||
    formState.structure_type === StructureType.WITH_SCHEDULES;

  const showSchedulePicker = formState.structure_type === StructureType.WITH_SCHEDULES;

  // Stable callback for KeyIssuesAccordion to prevent infinite loops
  const handleKeyIssuesChange = useCallback((keyIssues: KeyIssues) => {
    updateField('key_issues', keyIssues);
  }, []);

  return (
    <div
      className="configuration-form"
      data-testid="configuration-form"
      role="group"
    >
      <section
        className="form-section core-settings"
        data-testid="core-settings-section"
      >
        <h2>Core Settings</h2>

        {/* Stance Radio Buttons */}
        <fieldset className="form-fieldset" role="group" aria-labelledby="stance-legend">
          <legend id="stance-legend" className="fieldset-legend">Stance</legend>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="stance"
                value={DraftingStance.CUSTOMER}
                checked={formState.stance === DraftingStance.CUSTOMER}
                onChange={() => updateField('stance', DraftingStance.CUSTOMER)}
              />
              <span className="radio-text">Customer-favoured</span>
            </label>

            <label className="radio-label">
              <input
                type="radio"
                name="stance"
                value={DraftingStance.BALANCED}
                checked={formState.stance === DraftingStance.BALANCED}
                onChange={() => updateField('stance', DraftingStance.BALANCED)}
              />
              <span className="radio-text">Balanced</span>
            </label>

            <label className="radio-label">
              <input
                type="radio"
                name="stance"
                value={DraftingStance.VENDOR}
                checked={formState.stance === DraftingStance.VENDOR}
                onChange={() => updateField('stance', DraftingStance.VENDOR)}
              />
              <span className="radio-text">Vendor-favoured</span>
            </label>
          </div>
        </fieldset>

        {/* Complexity Radio Buttons */}
        <fieldset className="form-fieldset" role="group" aria-labelledby="complexity-legend">
          <legend id="complexity-legend" className="fieldset-legend">Complexity</legend>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="complexity"
                value={DraftingComplexity.SIMPLE}
                checked={formState.complexity === DraftingComplexity.SIMPLE}
                onChange={() => updateField('complexity', DraftingComplexity.SIMPLE)}
              />
              <span className="radio-text">Simple <span className="radio-description">(&lt;10 pages)</span></span>
            </label>

            <label className="radio-label">
              <input
                type="radio"
                name="complexity"
                value={DraftingComplexity.MODERATE}
                checked={formState.complexity === DraftingComplexity.MODERATE}
                onChange={() => updateField('complexity', DraftingComplexity.MODERATE)}
              />
              <span className="radio-text">Moderate <span className="radio-description">(10-30 pages)</span></span>
            </label>

            <label className="radio-label">
              <input
                type="radio"
                name="complexity"
                value={DraftingComplexity.COMPLEX}
                checked={formState.complexity === DraftingComplexity.COMPLEX}
                onChange={() => updateField('complexity', DraftingComplexity.COMPLEX)}
              />
              <span className="radio-text">Complex <span className="radio-description">(30+ pages)</span></span>
            </label>
          </div>
        </fieldset>

        {/* Role & Context Textarea */}
        <div className="form-field">
          <label htmlFor="context" className="field-label">
            Role &amp; Context
          </label>
          <textarea
            id="context"
            name="context"
            className="form-textarea"
            rows={4}
            value={formState.context}
            onChange={(e) => updateField('context', e.target.value)}
            placeholder="Describe the purpose of this contract (e.g., 'Software licensing agreement for enterprise SaaS platform with focus on data protection')"
          />
        </div>

        {/* Industry Dropdown */}
        <div className="form-field">
          <label htmlFor="industry" className="field-label">
            Industry
          </label>
          <select
            id="industry"
            name="industry"
            className="form-select"
            value={formState.industry}
            onChange={(e) => updateField('industry', e.target.value)}
          >
            <option value="">Select an industry...</option>
            {INDUSTRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Product/Service Type Text Input */}
        <div className="form-field">
          <label htmlFor="product_service" className="field-label">
            Product/Service Type
          </label>
          <input
            type="text"
            id="product_service"
            name="product_service"
            className="form-input"
            value={formState.product_service}
            onChange={(e) => updateField('product_service', e.target.value)}
            placeholder="e.g., Cloud-based CRM Platform, IT Consulting Services"
          />
        </div>
      </section>

      {/* ====================================================================== */}
      {/* Section B: Contract Structure */}
      {/* ====================================================================== */}
      <section
        className="form-section contract-structure"
        data-testid="contract-structure-section"
      >
        <h2>Contract Structure</h2>

        {/* Structure Type Radio Buttons */}
        <fieldset className="form-fieldset" role="group" aria-labelledby="structure-type-legend">
          <legend id="structure-type-legend" className="fieldset-legend">Structure Type</legend>
          <div className="radio-group radio-group-vertical">
            <label className="radio-label">
              <input
                type="radio"
                name="structure_type"
                value={StructureType.MAIN_ONLY}
                checked={formState.structure_type === StructureType.MAIN_ONLY}
                onChange={() => updateField('structure_type', StructureType.MAIN_ONLY)}
              />
              <span className="radio-text">Main Body only</span>
            </label>

            <label className="radio-label">
              <input
                type="radio"
                name="structure_type"
                value={StructureType.WITH_COMMERCIAL}
                checked={formState.structure_type === StructureType.WITH_COMMERCIAL}
                onChange={() => updateField('structure_type', StructureType.WITH_COMMERCIAL)}
              />
              <span className="radio-text">Main Body + Commercial Terms</span>
            </label>

            <label className="radio-label">
              <input
                type="radio"
                name="structure_type"
                value={StructureType.WITH_SCHEDULES}
                checked={formState.structure_type === StructureType.WITH_SCHEDULES}
                onChange={() => updateField('structure_type', StructureType.WITH_SCHEDULES)}
              />
              <span className="radio-text">Main Body + Commercial Terms + Schedules</span>
            </label>
          </div>
        </fieldset>

        {/* Commercial Position Toggle (conditional) */}
        {showCommercialPosition && (
          <div
            className="commercial-position-toggle expanded"
            data-testid="commercial-position-toggle"
          >
            <fieldset className="form-fieldset" role="group" aria-labelledby="commercial-position-legend">
              <legend id="commercial-position-legend" className="fieldset-legend">
                Commercial Terms Position
              </legend>
              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="radio"
                    name="commercial_position"
                    value="front"
                    checked={formState.commercial_position === 'front'}
                    onChange={() => updateField('commercial_position', 'front')}
                  />
                  <span className="toggle-text">Front</span>
                </label>

                <label className="toggle-label">
                  <input
                    type="radio"
                    name="commercial_position"
                    value="back"
                    checked={formState.commercial_position === 'back'}
                    onChange={() => updateField('commercial_position', 'back')}
                  />
                  <span className="toggle-text">Back</span>
                </label>
              </div>
            </fieldset>
          </div>
        )}

        {/* Schedule Picker (conditional) */}
        {showSchedulePicker && (
          <div
            className="schedule-picker expanded"
            data-testid="schedule-picker"
          >
            <fieldset className="form-fieldset" role="group" aria-labelledby="schedules-legend">
              <legend id="schedules-legend" className="fieldset-legend">
                Select Schedules to Include
              </legend>
              <div className="checkbox-grid">
                {SCHEDULE_OPTIONS.map((option) => (
                  <label key={option.value} className="checkbox-label">
                    <input
                      type="checkbox"
                      id={`schedule-${option.value}`}
                      name="schedules"
                      value={option.value}
                      checked={formState.schedules.includes(option.value)}
                      onChange={() => toggleSchedule(option.value)}
                    />
                    <span className="checkbox-text">{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        )}
      </section>

      {/* ====================================================================== */}
      {/* Section C: Basic Details */}
      {/* ====================================================================== */}
      <section
        className="form-section basic-details"
        data-testid="basic-details-section"
      >
        <h2>Basic Details</h2>

        {/* Title of Agreement */}
        <div className="form-field">
          <label htmlFor="agreement_title" className="field-label">
            Title of Agreement
          </label>
          <input
            type="text"
            id="agreement_title"
            name="agreement_title"
            className="form-input"
            value={formState.basic_issues.title || ''}
            onChange={(e) => updateBasicIssue('title', e.target.value)}
            placeholder={
              isSow
                ? 'e.g., Statement of Work No. 3 – CRM Implementation'
                : 'e.g., Master Services Agreement, Software Licence Agreement'
            }
          />
        </div>

        {/* Commencement Date */}
        <div className="form-field">
          <label htmlFor="commencement_date" className="field-label">
            Commencement Date
          </label>
          <input
            type="date"
            id="commencement_date"
            name="commencement_date"
            className="form-input"
            value={formState.basic_issues.commencement_date || ''}
            onChange={(e) => updateBasicIssue('commencement_date', e.target.value)}
          />
        </div>

        {/* Plan 253: a SoW sits under a master agreement. Without these fields
            the model invented one; the prompt now uses these or a neutral
            "Master Agreement" reference. */}
        {isSow && (
          <>
            <div className="form-field">
              <label htmlFor="master_agreement_title" className="field-label">
                Master agreement title
              </label>
              <input
                type="text"
                id="master_agreement_title"
                name="master_agreement_title"
                className="form-input"
                value={formState.basic_issues.master_agreement?.title || ''}
                onChange={(e) =>
                  updateBasicIssue('master_agreement', {
                    ...formState.basic_issues.master_agreement,
                    title: e.target.value,
                  })
                }
                placeholder="e.g., Master Services Agreement"
              />
            </div>
            <div className="form-field">
              <label htmlFor="master_agreement_date" className="field-label">
                Master agreement date
              </label>
              <input
                type="date"
                id="master_agreement_date"
                name="master_agreement_date"
                className="form-input"
                value={formState.basic_issues.master_agreement?.date || ''}
                onChange={(e) =>
                  updateBasicIssue('master_agreement', {
                    ...formState.basic_issues.master_agreement,
                    date: e.target.value,
                  })
                }
              />
            </div>
          </>
        )}

        {/* Jurisdiction Dropdown */}
        <div className="form-field">
          <label htmlFor="jurisdiction" className="field-label">
            Jurisdiction
          </label>
          <select
            id="jurisdiction"
            name="jurisdiction"
            className="form-select"
            value={formState.basic_issues.jurisdiction || ''}
            onChange={(e) => updateBasicIssue('jurisdiction', e.target.value)}
          >
            <option value="">Select a jurisdiction...</option>
            {JURISDICTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Party 1 Collapsible Section */}
        <div
          className="party-section"
          data-testid="party1-section"
          aria-label="Party 1 Details"
        >
          <h3 className="party-header">{isSow ? 'Party 1 — Customer' : 'Party 1'}</h3>
          <div className="party-fields">
            <div className="form-field">
              <label htmlFor="party1_name" className="field-label">
                Party 1 Name
              </label>
              <input
                type="text"
                id="party1_name"
                name="party1_name"
                className="form-input"
                value={formState.basic_issues.party1?.name || ''}
                onChange={(e) => updatePartyInfo('party1', 'name', e.target.value)}
                placeholder="e.g., Acme Corporation Pty Ltd"
              />
            </div>

            <div className="form-field">
              <label htmlFor="party1_short_name" className="field-label">
                Party 1 Short Name
              </label>
              <input
                type="text"
                id="party1_short_name"
                name="party1_short_name"
                className="form-input"
                value={formState.basic_issues.party1?.short_name || ''}
                onChange={(e) => updatePartyInfo('party1', 'short_name', e.target.value)}
                placeholder="e.g., Acme"
              />
            </div>

            <div className="form-field">
              <label htmlFor="party1_address" className="field-label">
                Party 1 Address
              </label>
              <input
                type="text"
                id="party1_address"
                name="party1_address"
                className="form-input"
                value={formState.basic_issues.party1?.address || ''}
                onChange={(e) => updatePartyInfo('party1', 'address', e.target.value)}
                placeholder="e.g., 123 Business Street, Sydney NSW 2000"
              />
            </div>

            <div className="form-field">
              <label htmlFor="party1_company_number" className="field-label">
                Party 1 Company Number (ABN/ACN)
              </label>
              <input
                type="text"
                id="party1_company_number"
                name="party1_company_number"
                className="form-input"
                value={formState.basic_issues.party1?.company_number || ''}
                onChange={(e) => updatePartyInfo('party1', 'company_number', e.target.value)}
                placeholder="e.g., 12 345 678 901"
              />
            </div>
          </div>
        </div>

        {/* Party 2 Collapsible Section */}
        <div
          className="party-section"
          data-testid="party2-section"
          aria-label="Party 2 Details"
        >
          <h3 className="party-header">{isSow ? 'Party 2 — Supplier' : 'Party 2'}</h3>
          <div className="party-fields">
            <div className="form-field">
              <label htmlFor="party2_name" className="field-label">
                Party 2 Name
              </label>
              <input
                type="text"
                id="party2_name"
                name="party2_name"
                className="form-input"
                value={formState.basic_issues.party2?.name || ''}
                onChange={(e) => updatePartyInfo('party2', 'name', e.target.value)}
                placeholder="e.g., Tech Solutions Inc"
              />
            </div>

            <div className="form-field">
              <label htmlFor="party2_short_name" className="field-label">
                Party 2 Short Name
              </label>
              <input
                type="text"
                id="party2_short_name"
                name="party2_short_name"
                className="form-input"
                value={formState.basic_issues.party2?.short_name || ''}
                onChange={(e) => updatePartyInfo('party2', 'short_name', e.target.value)}
                placeholder="e.g., Tech Solutions"
              />
            </div>

            <div className="form-field">
              <label htmlFor="party2_address" className="field-label">
                Party 2 Address
              </label>
              <input
                type="text"
                id="party2_address"
                name="party2_address"
                className="form-input"
                value={formState.basic_issues.party2?.address || ''}
                onChange={(e) => updatePartyInfo('party2', 'address', e.target.value)}
                placeholder="e.g., 456 Innovation Drive, Melbourne VIC 3000"
              />
            </div>

            <div className="form-field">
              <label htmlFor="party2_company_number" className="field-label">
                Party 2 Company Number (ABN/ACN)
              </label>
              <input
                type="text"
                id="party2_company_number"
                name="party2_company_number"
                className="form-input"
                value={formState.basic_issues.party2?.company_number || ''}
                onChange={(e) => updatePartyInfo('party2', 'company_number', e.target.value)}
                placeholder="e.g., 98 765 432 109"
              />
            </div>
          </div>
        </div>

        {/* Third Parties */}
        <div className="form-field">
          <label htmlFor="third_parties" className="field-label">
            Third Parties
          </label>
          <textarea
            id="third_parties"
            name="third_parties"
            className="form-textarea"
            rows={3}
            value={formState.basic_issues.third_parties || ''}
            onChange={(e) => updateBasicIssue('third_parties', e.target.value)}
            placeholder="List any third party beneficiaries or related parties..."
          />
          <span className="field-helper">(Optional)</span>
        </div>

        {/* Term Section */}
        <div className="term-section">
          <h3 className="section-subheader">Term</h3>
          <div className="term-fields">
            <div className="form-field">
              <label htmlFor="term_start" className="field-label">
                Start Date
              </label>
              <input
                type="date"
                id="term_start"
                name="term_start"
                className="form-input"
                value={formState.basic_issues.term_start || ''}
                onChange={(e) => updateBasicIssue('term_start', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label htmlFor="term_end" className="field-label">
                End Date
              </label>
              <input
                type="date"
                id="term_end"
                name="term_end"
                className="form-input"
                value={formState.basic_issues.term_end || ''}
                onChange={(e) => updateBasicIssue('term_end', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Auto-Renewal Toggle */}
        <div className="form-field auto-renewal-field">
          <label className="checkbox-toggle-label">
            <input
              type="checkbox"
              id="auto_renewal"
              name="auto_renewal"
              checked={formState.basic_issues.auto_renewal || false}
              onChange={(e) => updateBasicIssue('auto_renewal', e.target.checked)}
            />
            <span className="toggle-text">Auto-Renewal</span>
          </label>
        </div>

        {/* Renewal Options (conditional) */}
        {formState.basic_issues.auto_renewal && (
          <div className="renewal-options expanded">
            <div className="form-field">
              <label htmlFor="renewal_period" className="field-label">
                Renewal Period
              </label>
              <select
                id="renewal_period"
                name="renewal_period"
                className="form-select"
                value={formState.basic_issues.renewal_period || ''}
                onChange={(e) => updateBasicIssue('renewal_period', e.target.value)}
              >
                <option value="">Select renewal period...</option>
                {RENEWAL_PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="max_renewals" className="field-label">
                Maximum Renewals
              </label>
              <input
                type="number"
                id="max_renewals"
                name="max_renewals"
                className="form-input"
                min={1}
                value={formState.basic_issues.max_renewals ?? ''}
                onChange={(e) => updateBasicIssue('max_renewals', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="e.g., 3"
              />
            </div>
          </div>
        )}

        {/* Dispute Resolution Dropdown */}
        <div className="form-field">
          <label htmlFor="dispute_resolution" className="field-label">
            Dispute Resolution
          </label>
          <select
            id="dispute_resolution"
            name="dispute_resolution"
            className="form-select"
            value={formState.basic_issues.dispute_resolution || ''}
            onChange={(e) => updateBasicIssue('dispute_resolution', e.target.value as BasicIssues['dispute_resolution'])}
          >
            <option value="">Select dispute resolution method...</option>
            {DISPUTE_RESOLUTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* ====================================================================== */}
      {/* Section D: Key Issues */}
      {/* ====================================================================== */}
      {/* #1108: no data-testid here — KeyIssuesAccordion's own root already
          declares data-testid="key-issues-section", and two nested elements
          sharing one id makes getByTestId throw. The accordion owns it. */}
      <section className="form-section key-issues">
        <KeyIssuesAccordion
          onChange={handleKeyIssuesChange}
          initialValues={formState.key_issues}
          issueKeys={isSow ? SOW_KEY_ISSUES : undefined}
        />
      </section>

      {/* ====================================================================== */}
      {/* Section E: Base Document Upload (suppressed when hideBaseDocument) */}
      {/* ====================================================================== */}
      {!hideBaseDocument && (
        <section
          className="form-section base-document"
          data-testid="base-document-section"
        >
          <BaseDocumentUpload
            projectId={projectId}
            initialDocument={formState.base_document}
            onUploadComplete={(doc) => updateField('base_document', doc)}
            onUploadError={(error) => console.error('Base document upload error:', error)}
            onRemove={() => {
              updateField('base_document', undefined);
              if (onBaseDocumentSelected) {
                onBaseDocumentSelected(null);
              }
            }}
            onFileSelected={onBaseDocumentSelected}
            stagedFile={stagedBaseDocument}
          />
        </section>
      )}
    </div>
  );
};

export default ConfigurationForm;
